import React, { Component } from 'react';
import TagManager from 'react-gtm-module';
import { formatMinorToMajorUnits } from '@h/currency';
import withCurrencyConfig from '@c/WithCurrencyConfig';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as hookActions from 'checkout/actions/hooks';
import { getMainEvent, getSelectedEvent, isSeries } from '@b/checkout/selectors/event';
import { getSelectedDeliveryOption } from '@b/checkout/selectors/delivery';
import { getOrderTotal } from '@b/checkout/selectors/cart';
import isEqual from 'lodash/isEqual';
import { getSeriesConfig } from '@b/checkout/selectors/ui';
import { getSeriesOption } from '@v/series/selectors/eventSeries';
import { EMBED_TYPE_CALENDAR, EMBED_TYPE_TICKET_LIST, TICKETS_DISPLAY_LIST } from '@b/checkout/reducers/ui';
import { EcommerceShared } from './EcommerceShared';

class EcommerceManager extends Component {
  constructor(props) {
    super(props);

    this.state = {
      previousAdditionalFees: [],
      previousProductsImpressions: [],
      previousBundlesImpressions: [],
      previousPriceLevelsImpressions: [],
      hasSelectedEvent: false,
    }

    this.embedPort = null;
    this.initializeEmbedPort();

    this.props.addTicketsAddedToCartHook(this.onTicketsAdded);
    this.props.addProductsAddedToCartHook(this.onProductsAdded);
    this.props.addBundlesAddedToCartHook(this.onBundlesAdded);
    this.props.addReservationDataSetHook(this.onReservationDataSet);
    this.props.addReservationCompleteHook(this.onReservationComplete);
    this.props.addStateTransitionHook(this.onStateTransition);
    this.props.addGoToCheckoutHook(this.onGoToCheckout);
    this.props.addSetPriceLevelsHook(this.onPriceLevelsSet);
    this.props.addSetBundlesHook(this.onBundlesSet);
    this.props.addSetProductsHook(this.onProductsSet);
    this.props.addCalendarDateSelectedHook(this.onCalendarDateSelected);
    this.props.addCalendarTimeSelectedHook(this.onCalendarTimeSelected);
    this.props.addCalendarDateChangedHook(this.onCalendarDateChanged);
    this.props.addCalendarViewHook(this.onCalendarView);
    this.props.addTicketIncrementHook(this.onItemQuantityChange('ticket', 'increment'));
    this.props.addTicketDecrementHook(this.onItemQuantityChange('ticket', 'decrement'));
    this.props.addTicketChangeQuantityHook(this.onItemQuantityChange('ticket'));
    this.props.addProductIncrementHook(this.onItemQuantityChange('product', 'increment'));
    this.props.addProductDecrementHook(this.onItemQuantityChange('product', 'decrement'));
    this.props.addProductChangeQuantityHook(this.onItemQuantityChange('product'));
    this.props.addBundleIncrementHook(this.onItemQuantityChange('bundle', 'increment'));
    this.props.addBundleDecrementHook(this.onItemQuantityChange('bundle', 'decrement'));
    this.props.addBundleChangeQuantityHook(this.onItemQuantityChange('bundle'));
    this.props.addCheckoutOverlayChangedHook(this.onCheckoutOverlayChanged);
    this.props.addMembershipViewHook(this.onMembershipView);
    this.props.addMembershipSuccessSignInHook(this.onMembershipSuccessSignIn);
    this.props.addMembershipSkipSignInHook(this.onMembershipSkipSignIn);
    this.props.addMembershipGoToSignInHook(this.onMembershipGoToSignIn);
    this.props.addMembershipSignOutHook(this.onMembershipSignOut);
  }

  initializeEmbedPort = () => {
    if (this.props.isEmbed) {
      let channel = new MessageChannel();
      this.embedPort = channel.port1;
      window.top.postMessage("ecommercePort", window.top.location, [channel.port2]);
    }
  }

  componentDidMount() {
    if (this.props.currentRouteName === 'ticketPicker' && this.props.overlayVisible) {
      const dataLayer = this.getBaseDataLayer();
      dataLayer.ecommerce.impressions = this.getAllImpressions({
        priceLevels: this.props.priceLevels,
        bundles: this.props.bundles,
        products: this.props.products,
      });

      this.report(dataLayer);
    }

    this.report(EcommerceShared.getEventInfoLayer(this.props));
  }

  componentWillUnmount() {
    if (this.embedPort) {
      this.embedPort.close();
    }
  }

  pushToAnalyticsContainer = (data) => {
    this.props.reportEvent('ecommerce-manager')(data);
  }

  beforeReport = (dataLayer) => {
    if (dataLayer.ecommerce) {
      dataLayer.ecommerce.tickets_display_type = this.getFinalTicketsDisplayType();
    }
  }
  
  report = (dataLayer, analyticContainer = false) => {
    if (!dataLayer) {
      return;
    }

    this.beforeReport(dataLayer);

    TagManager.dataLayer({ dataLayer });

    analyticContainer && this.pushToAnalyticsContainer(dataLayer);

    this.sendToParentDataLayer(dataLayer);
  }

  sendToParentDataLayer = (dataLayer) => {
    if (this.props.isEmbed && this.embedPort && dataLayer) {
      const ecommerceEvent = {
        ...dataLayer,
        listingSlug: this.props.listingSlug,
      };

      this.embedPort.postMessage({
        action: 'ecommerce',
        data: ecommerceEvent
      });
    }
  }

  getBaseDataLayer = () => ({
    'ecommerce': {
      'currencyCode': this.getCurrencyCode(),
    }
  })

  getFinalTicketsDisplayType = () => {
    if (this.props.isEmbed) {
      if (this.props.embedType === EMBED_TYPE_TICKET_LIST) {
        return 'list';
      }

      if (this.props.embedType === EMBED_TYPE_CALENDAR) {
        return 'calendar';
      }

      return 'button';
    }

    if (this.props.isSeries) {
      return this.props.calendarOnListing ? 'calendar' : 'button';
    }

    return this.props?.ticketsDisplayType === TICKETS_DISPLAY_LIST ? 'list' : 'button';
  }

  getAllImpressions = ({
    priceLevels,
    bundles,
    products,
  }) => {
    const priceLevelImpressions = this.getItemsImpressions(priceLevels, 'level');
    const bundleImpressions = this.getItemsImpressions(bundles, 'bundle');
    const productImpressions = this.getItemsImpressions(products, 'product');

    return [
      ...priceLevelImpressions,
      ...bundleImpressions,
      ...productImpressions,
    ];
  }

  getIdPropertyName = (type) => {
    switch (type) {
      case 'level':
        return 'price_level_id';
      case 'bundle':
        return 'id';
      case 'product':
        return 'product_id';
      default:
        return 'id';
    }
  }

  getId = (item, type) => {
    const { mainEvent, selectedEvent, isSeries } = this.props;

    const getSeriesValues = () => {
      // bundles have really weird structure, so we need to handle it separately
      if (type === 'bundle' && !item.parent_id) {
        const bundle = this.props.bundles[item.bundle_id];
        const bundle_parent_id = bundle?.parent_id;

        return [
          mainEvent?.event_id,
          item.eventId || item.event_id || selectedEvent?.event_id, 
          type,
          bundle_parent_id,
          item.bundle_id || item.id,
        ];
      }

      return [
        mainEvent?.event_id,
        item.eventId || item.event_id || selectedEvent?.event_id, 
        type,
        item.parent_id,
        item[this.getIdPropertyName(type)],
      ];
    };

    const getSingleValues = () => {
      return [
        item.eventId || item.event_id || selectedEvent?.event_id,
        type,
        item[this.getIdPropertyName(type)],
      ];
    };

    return (isSeries ? getSeriesValues() : getSingleValues())
      .filter(Boolean)
      .join('-');
  }

  getItemsImpressions = (items, type) => {
    return Object.values(items).map((item, index) => {
      return {
        'name': item.name,
        'id': this.getId(item, type),
        'price': formatMinorToMajorUnits({ amount: item.price.price || item.price.amount, currency: item.price.currency }),
        'brand': this.props.contactInfo.name,
        'category': this.props.eventName,
        'position': item.position || (index + 1),
        'metric4': formatMinorToMajorUnits({ amount: item.service_fee.price, currency: item.service_fee.currency }),
      };
    });
  }

  getCurrencyCode = () => {
    if (this.props.reservationCurrency) {
      return this.props.reservationCurrency;
    }

    if (Object.values(this.props.priceLevels).length > 0) {
      return this.props.priceLevels[Object.keys(this.props.priceLevels)[0]].price.currency;
    }
    return 'USD';
  }

  addTicketsToProducts = (currentLayer, tickets, action) => {
    if (!currentLayer[action]) {
      currentLayer[action] = {};
    }

    const ticketsGroupedByPriceLevelId = tickets.reduce((acc, ticket) => {
      const key = `${ticket.event_id}-level-${ticket.price_level_id}`;
      if (!acc[key]) {
        acc[key] = ticket;
      }
      acc[key].quantity = (acc[key].quantity || 0) + 1;
      return acc;
    }, {});
    currentLayer[action].products = [
      ...currentLayer[action]?.products || [],
      ...Object.values(ticketsGroupedByPriceLevelId).map((ticket) => ({
        name: ticket.price_level_name,
        id: this.getId(ticket, 'level'),
        price: formatMinorToMajorUnits({ amount: ticket.cost.amount, currency: ticket.cost.currency }),
        category: ticket.event_name,
        brand: this.props.contactInfo.name,
        quantity: ticket.quantity,
        metric4: formatMinorToMajorUnits({ amount: ticket.buyer_fee.amount, currency: ticket.buyer_fee.currency }),
      })),
    ];
  }

  addProductsToProducts = (currentLayer, products, action) => {
    if (!currentLayer[action]) {
      currentLayer[action] = {};
    }

    const productsGroupedByProductId = products.reduce((acc, product) => {
      const key = `${product.product_id}-config-${product.configuration_id}`;
      if (!acc[key]) {
        acc[key] = product;
      }
      acc[key].quantity = (acc[key].quantity || 0) + 1;
      return acc;
    }, {});

    currentLayer[action].products = [
      ...currentLayer[action]?.products || [],
      ...Object.values(productsGroupedByProductId).map((product) => ({
          name: product.product_name,
          id: this.getId(product, 'product'),
          price: formatMinorToMajorUnits({ amount: product.cost.amount, currency: product.cost.currency }),
          category: product.event_name,
          brand: this.props.contactInfo.name,
          variant: product.display_name,
          quantity: product.quantity,
          metric4: formatMinorToMajorUnits({ amount: product.buyer_fee.amount, currency: product.buyer_fee.currency }),
      })),
    ];
  }

  addBundlesToProducts = (currentLayer, bundles, action) => {
    if (!currentLayer[action]) {
      currentLayer[action] = {};
    }

    const bundleGroupedByBundleId = bundles.reduce((acc, bundle) => {
      const key = `${bundle.event_id}-bundle-${bundle.bundle_id}`;
      if (!acc[key]) {
        acc[key] = bundle;
      }
      acc[key].quantity = (acc[key].quantity || 0) + 1;
      return acc;
    }, {});

    currentLayer[action].products = [
      ...currentLayer[action]?.products || [],
      ...Object.values(bundleGroupedByBundleId).map((bundle) => ({
        name: bundle.name,
        id: this.getId(bundle, 'bundle'),
        price: formatMinorToMajorUnits({ amount: bundle.cost.amount, currency: bundle.cost.currency }),
        category: bundle.event_name,
        brand: this.props.contactInfo.name,
        variant: bundle.display_name,
        quantity: bundle.quantity || 1,
        metric4: formatMinorToMajorUnits({ amount: bundle.buyer_fee.amount, currency: bundle.buyer_fee.currency }),
      })),
    ];
  }

  addFeesToProducts = (currentLayer, fees, action) => {
    if (!currentLayer[action]) {
      currentLayer[action] = {};
    }
    currentLayer[action].products = [
      ...currentLayer[action]?.products || [],
      ...fees.map((fee) => ({
        name: fee.name,
        id: fee.name,
        price: formatMinorToMajorUnits({ amount: fee.fee_details.amount, currency: fee.fee_details.currency }),
        category: 'Fee',
        quantity: 1,
      })),
    ];
  }

  onTicketsAdded = (tickets) => {
    const dataLayer = this.getBaseDataLayer();

    this.addTicketsToProducts(dataLayer.ecommerce, tickets, 'add');

    this.report(dataLayer);
  }

  onProductsAdded = (products) => {
    const dataLayer = this.getBaseDataLayer();

    this.addProductsToProducts(dataLayer.ecommerce, products, 'add');

    this.report(dataLayer);
  }

  onBundlesAdded = (bundles) => {
    const dataLayer = this.getBaseDataLayer();

    this.addBundlesToProducts(dataLayer.ecommerce, bundles, 'add');

    this.report(dataLayer);
  }

  onReservationDataSet = (reservation) => {
    if (JSON.stringify(reservation?.attributes?.additional_fees) === JSON.stringify(this.state.previousAdditionalFees)) {
      return;
    }

    this.setState({ previousAdditionalFees: reservation?.attributes?.additional_fees || [] });

    const dataLayer = this.getBaseDataLayer();

    this.addFeesToProducts(dataLayer.ecommerce, reservation?.attributes?.additional_fees, 'add');

    this.report(dataLayer);
  }

  onReservationComplete = () => {
    const dataLayer = this.getBaseDataLayer();
    dataLayer.ecommerce.purchase = {
      'actionField': {
        'id': this.props.reservation?.confirmation_number,
        'affiliation': this.props.contactInfo.name,
        'revenue': formatMinorToMajorUnits({ amount: this.props.orderTotal, currency: this.props.reservationCurrency }),
        'tax': formatMinorToMajorUnits({ amount: this.props.reservation?.total_fees?.amount || 0, currency: this.props.reservationCurrency }),
        'shipping': formatMinorToMajorUnits({ amount: this.props.reservation?.total_delivery_fees?.amount || 0, currency: this.props.reservationCurrency }),
        'coupon': this.props.codes.join(', ') || '',
      },
      'products': [],
    };
    dataLayer.event = 'purchase';
    this.addTicketsToProducts(dataLayer.ecommerce, this.props.tickets, 'purchase');
    this.addProductsToProducts(dataLayer.ecommerce, this.props.productsSales, 'purchase');
    this.addBundlesToProducts(dataLayer.ecommerce, this.props.bundlesSales, 'purchase');
    this.addFeesToProducts(dataLayer.ecommerce, this.props.reservation?.additional_fees || [], 'purchase');

    this.report(dataLayer);
  }

  onStateTransition = (state) => {
    if (state === 'checkout-2') {
      const secondStepDataLayer = this.getBaseDataLayer();
      secondStepDataLayer.ecommerce.checkout = {
        'actionField': {
          'step': 2,
          'option': this.props.deliveryOptionName,
        },
      };
      secondStepDataLayer.event = 'checkoutOption';

      TagManager.dataLayer({
        dataLayer: secondStepDataLayer,
      });

      const thirdStepDataLayer = this.getBaseDataLayer();
      thirdStepDataLayer.ecommerce.checkout = {
        'actionField': {
          'step': 3,
          'option': 'Credit Card'
        },
      };
      thirdStepDataLayer.event = 'checkoutOption';

      this.report({ dataLayer: thirdStepDataLayer });
    }

    if (state === 'ticketPicker') {
      const dataLayer = this.getBaseDataLayer();
      dataLayer.ecommerce.impressions = this.getAllImpressions({
        priceLevels: this.props.priceLevels,
        bundles: this.props.bundles,
        products: this.props.products,
      });

      this.report(dataLayer);
    }
  }

  onGoToCheckout = () => {
    const reservationSessionKey = `EcommerceManager_${this.props.reservation?.confirmation_number}`;

    if (sessionStorage.getItem(reservationSessionKey)) {
      return;
    }

    const dataLayer = this.getBaseDataLayer();

    this.addTicketsToProducts(dataLayer.ecommerce, this.props.tickets, 'checkout');
    this.addProductsToProducts(dataLayer.ecommerce, this.props.productsSales, 'checkout');
    this.addBundlesToProducts(dataLayer.ecommerce, this.props.bundlesSales, 'checkout');
    this.addFeesToProducts(dataLayer.ecommerce, this.props.reservation?.additional_fees || [], 'checkout');

    dataLayer.ecommerce.checkout.actionField = {
      'step': 1,
    };

    dataLayer.event = 'checkout';

    this.report(dataLayer);

    sessionStorage.setItem(reservationSessionKey, '1');
  }

  onPriceLevelsSet = (priceLevels, empty) => {
    if (empty || !this.props.overlayVisible) {
      return;
    }
    
    const impressions = this.getItemsImpressions(priceLevels, 'level');

    if (isEqual(impressions, this.state.previousPriceLevelsImpressions)) {
      return;
    }

    this.setState({ previousPriceLevelsImpressions: impressions });
    
    const dataLayer = this.getBaseDataLayer();
    dataLayer.ecommerce.impressions = impressions;

    this.report(dataLayer);
  }

  onBundlesSet = (bundles, empty) => {
    if (empty || !this.props.overlayVisible) {
      return;
    }

    const impressions = this.getItemsImpressions(bundles, 'bundle');

    if (isEqual(impressions, this.state.previousBundlesImpressions)) {
      return;
    }

    this.setState({ previousBundlesImpressions: impressions });

    const dataLayer = this.getBaseDataLayer();
    dataLayer.ecommerce.impressions = impressions;

    this.report(dataLayer);
  }

  onProductsSet = (products, empty) => {
    if (empty || !this.props.overlayVisible) {
      return;
    }

    const impressions = this.getItemsImpressions(products, 'product');

    if (isEqual(impressions, this.state.previousProductsImpressions)) {
      return;
    }

    this.setState({ previousProductsImpressions: impressions });

    const dataLayer = this.getBaseDataLayer();
    dataLayer.ecommerce.impressions = impressions;

    this.report(dataLayer);
  }

  onCalendarDateSelected = (date) => {
    const dataLayer = EcommerceShared.getCalendarDateSelectedLayer(date, this.props);

    this.report(dataLayer, false);
  }

  onCalendarDateChanged = () => {
    if (!this.props.overlayVisible) {
      return;
    }

    this.report(EcommerceShared.getReportDateChangedLayer(this.props));
  }

  onCalendarTimeSelected = ({ event }) => {
    if (!this.props.overlayVisible) {
      return;
    }

    if (!event) {
      if (this.state.hasSelectedEvent) {
        this.setState({ hasSelectedEvent: false });

        this.report(EcommerceShared.getReportDateChangedLayer(this.props));
        return;
      }
      return;
    }

    this.setState({ hasSelectedEvent: true });

    this.report(EcommerceShared.getReportCalendarTimeSelectedLayer(event, this.props));
  }

  onCalendarView = (data) => {
    this.report(EcommerceShared.getCalendarViewLayer(data, this.props));
  }

  onItemQuantityChange = (type, action) => (id, value) => {
    this.report(EcommerceShared.getItemQuantityChangeLayer(type, action, id, value, this.props));
  }

  onCheckoutOverlayChanged = (overlayVisible) => {
    if (!overlayVisible) {
      this.report(EcommerceShared.getEventInfoLayer(this.props));
    }
  }

  getMembershipEventDataLayer = (event) => {
    const dataLayer = this.getBaseDataLayer();

    dataLayer.event = event;
    dataLayer.ecommerce = {
      event_id: this.props.selectedEvent?.event_id || '',
      event_name: this.props.selectedEvent?.name || '',
      listing_slug: this.props.listingSlug || '',
      source_url: window.top.location.href,
    };

    return dataLayer;
  }

  onMembershipView = () => {
      this.report(this.getMembershipEventDataLayer('memberPageViewGate'));
  }

  onMembershipSuccessSignIn = () => {
      this.report(this.getMembershipEventDataLayer('memberSignInGate'));
  }

  onMembershipSkipSignIn = (value) => {
    if (!this.props.skipMembership && value) {
      this.report(this.getMembershipEventDataLayer('membershipSkipSignIn'));
    }
  }

  onMembershipGoToSignIn = () => {
      this.report(this.getMembershipEventDataLayer('memberSignInEvent'));
  }

  onMembershipSignOut = () => {
      this.report(this.getMembershipEventDataLayer('memberSignOutEvent'));
  }

  render() {
    return null;
  }
}

const mapStateToProps = (state) => {
  const mainEvent = getMainEvent(state)?.event;
  const selectedEvent = getSelectedEvent(state)?.event;

  return {
    mainEvent: mainEvent ?? null,
    selectedEvent: selectedEvent ?? null,
    isSeries: isSeries(state),
    eventName: mainEvent?.name || '',
    priceLevels: state.priceLevel.levels,
    bundles: state.bundles.bundles,
    products: state.products.products,
    contactInfo: state.contactInfo,
    tickets: state.tickets.tickets,
    productsSales: state.productSales.products,
    bundlesSales: state.bundleSales.bundles,
    reservation: state.reservation,
    reservationCurrency: state.reservation.currency,
    deliveryOptionName: getSelectedDeliveryOption(state)?.name || '',
    orderTotal: getOrderTotal(state)?.amount ?? 0,
    codes: state.coupon.codes || [],
    currentRouteName: state.ui.currentRouteName,
    overlayVisible: state.ui.overlay,
    calendarDisplayType: getSeriesConfig(state).calendarType,
    calendarOnListing: !!getSeriesOption("calendarOnListing", false)(state),
    ticketsDisplayType: state.ui.listingConfig.listings?.ticketsDisplayType,
    embedType: state.ui.embedType,
    isEmbed: state.ui.isEmbed,
    listingSlug: state.ui.listingSlug,
    skipMembership: state.ui.skipMembership,
  };
};

const mapDispatchToProps = dispatch => {
    const actions = {};
    Object.keys(hookActions).forEach(hookActionName => {
        actions[hookActionName] = hookActions[hookActionName];
    });
    return bindActionCreators(actions, dispatch);
}

export default withCurrencyConfig(connect(mapStateToProps, mapDispatchToProps)(EcommerceManager));
